import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import { EditOutlined } from '@ant-design/icons';
import { Content } from 'antd/lib/layout/layout';
import { RootState } from '../../reducers/reducers';
import { selectOrgSettings } from '../../selectors/selectors';
import { OrgComponentProps } from '../../typescriptHelpers';
import { IssueSourceType } from '@/types';
import { displayTextForIssueSourceType } from '../../helpers/displayHelpers';
import { useUpdateTriageDisabledSourcesMutation } from '@/middlewares/defaultApi';
import { updateOrgSettingsSuccessAction } from '../../actions/orgSettings';
import { globalMessage } from '@/utils/globalMessage';

const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    flex: 1,
    minHeight: 80,
};

const selectableSourceTypes = Object.values(IssueSourceType).filter((type) => type !== IssueSourceType.Runway);

const sourceTypeOptions = selectableSourceTypes.map((type) => ({
    label: displayTextForIssueSourceType(type),
    value: type,
}));

const enabledSourcesFromDisabled = (disabledSources: IssueSourceType[] | undefined): IssueSourceType[] =>
    selectableSourceTypes.filter((type) => !(disabledSources ?? []).includes(type));

const TriageSettings: React.FC<OrgComponentProps> = ({ orgId }) => {
    const dispatch = useDispatch();
    const orgSettings = useSelector((state: RootState) => selectOrgSettings(state, orgId));
    const savedEnabledSources = enabledSourcesFromDisabled(orgSettings?.triageSettings?.disabledSources);

    const [sourcesModalOpen, setSourcesModalOpen] = React.useState(false);
    const [selectedSources, setSelectedSources] = React.useState<IssueSourceType[]>(savedEnabledSources);

    const [updateTriageDisabledSources, { isLoading, data: updateResponse }] = useUpdateTriageDisabledSourcesMutation();
    const updatedOrg = updateResponse?.success ? updateResponse.data : undefined;
    const updateError = updateResponse?.errorMessage;

    const openSourcesModal = () => {
        setSelectedSources(savedEnabledSources);
        setSourcesModalOpen(true);
    };

    const onSaveSources = () => {
        const disabledSources = selectableSourceTypes.filter((type) => !selectedSources.includes(type));
        updateTriageDisabledSources({ orgId, disabledSources });
    };

    React.useEffect(() => {
        if (updatedOrg) {
            // Reflect the saved settings immediately rather than waiting on the org WS update.
            dispatch(updateOrgSettingsSuccessAction(updatedOrg));
            globalMessage.success({ content: 'Triage sources updated!', key: 'triageSourcesUpdated' });
            setSourcesModalOpen(false);
        }
    }, [updatedOrg, dispatch]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    return (
        <Content>
            <div style={{ maxWidth: 900, width: '100%', marginTop: 12 }}>
                <div style={rowStyle}>
                    <Space direction="vertical" size={4}>
                        <Text strong>Triage sources</Text>
                        <Text type="secondary">Choose which sources Triage will use to detect issues.</Text>
                    </Space>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={openSourcesModal}>
                        Edit
                    </Button>
                </div>
            </div>

            <Modal
                title="Edit Triage sources"
                open={sourcesModalOpen}
                okText="Save"
                cancelButtonProps={{ disabled: isLoading }}
                okButtonProps={{ loading: isLoading }}
                onOk={onSaveSources}
                onCancel={() => setSourcesModalOpen(false)}
            >
                <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 8 }}>
                    <Text>Select which sources Triage will use to detect issues</Text>
                    <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Select sources"
                        options={sourceTypeOptions}
                        value={selectedSources}
                        onChange={setSelectedSources}
                    />
                    <Text type="secondary">Runway will not save historical data for excluded sources.</Text>
                </Space>
            </Modal>
        </Content>
    );
};

export default TriageSettings;
